/**
 * Surfaces a failed or wedged planning run so the UI can stop pretending work
 * is still in flight.
 *
 * Two things can leave the progress tracker spinning forever:
 *
 *  1. The run ends with a failure status. `planning_complete` carries it, but
 *     every terminal status other than `cancelled` was treated as a clean
 *     finish, so an errored run looked identical to a successful one.
 *  2. The run dies without emitting any terminal event at all — a provider that
 *     keeps timing out, or a worker that stops making progress. Nothing ever
 *     clears `is_active`, so the spinner runs until the page is reloaded.
 *
 * Case 2 is why this watches elapsed time rather than trusting events alone:
 * silence is the failure signal, and only a clock can observe silence.
 *
 * Silence alone cannot tell a wedged run from a slow step (a page repair on a
 * slow model stream once took 18 minutes). When the server still reports the
 * step as running, the notice says so instead of "stopped responding": the
 * server refuses a Retry in that state, so Retry would be a dead button.
 */
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { CLIENT_TIMEOUT_DEFAULTS, getClientTimeout } from '../services/clientTimeoutConfig';
import { eventService } from '../services/eventService';

/** Statuses that mean the run stopped without delivering its work. */
const FAILURE_STATUSES = new Set(['error', 'failed', 'planning_failed']);

/**
 * Events that prove the backend is still *making progress* on this run.
 *
 * Deliberately excludes `ai_overview_text`: the Tech Lead cheerleader emits it
 * every ~15s specifically to fill idle gaps, so it keeps arriving long after
 * real work has stopped. Counting it as progress would make this watchdog blind
 * to exactly the wedged runs it exists to catch. Only state transitions count.
 */
const PROGRESS_EVENTS = [
  'agent_log',
  'design_pages',
  'agent_state_restore',
  'build_phases_update',
  'task_update',
] as const;

/**
 * How long a run may go without a state transition before it counts as wedged.
 * Generous on purpose: an observed page repair plus its quality retries ran
 * about four minutes, so this leaves roughly 2.5x headroom. A false "it failed"
 * is worse than a late one. This is the default; the admin timeout panel can
 * change it, and the watchdog reads the configured value each time it arms.
 */
export const PLANNING_STALL_TIMEOUT_MS = CLIENT_TIMEOUT_DEFAULTS.planningStallTimeoutMs;

const STALL_MESSAGE = 'Planning stopped responding. The last step never reported back.';
const DEFAULT_FAILURE_MESSAGE = 'Planning failed before it could finish.';

/** Copy for a quiet run whose step the server still reports as running. */
export const stillWorkingMessage = (minutes: number): string => (
  `Planning is still working on the last step. It hasn't reported in ${minutes} min. `
  + 'You can keep waiting or press Stop.'
);

/**
 * - `failed`: the run reported a failure status.
 * - `stalled`: it went quiet and nothing says it is still running; Retry is offered.
 * - `slow`: it went quiet, but the server still reports its step as running. Not
 *   a failure, so the run keeps reading as in progress and Stop stays available.
 */
export type PlanningRunNoticeKind = 'failed' | 'stalled' | 'slow';

export interface PlanningRunFailure {
  /** User-facing copy, or null while the run is healthy. */
  message: string | null;
  kind: PlanningRunNoticeKind | null;
  /** Clears the banner and re-arms the watchdog. */
  dismiss: () => void;
  /** The server refused a Retry because the run is still active: say so and re-arm the watchdog. */
  markStillWorking: () => void;
}

interface RunNotice {
  kind: PlanningRunNoticeKind;
  message: string;
}

interface PlanningCompleteEvent {
  project_id?: string | null;
  status?: string | null;
  error?: string | null;
}

type SetNotice = Dispatch<SetStateAction<RunNotice | null>>;

/** Without a server report, a quiet run reads as stalled. */
const NO_SERVER_REPORT = (): boolean => false;

const quietMinutes = (lastProgress: number, now: number): number => (
  Math.max(1, Math.floor((now - lastProgress) / 60_000))
);

const slowNotice = (current: RunNotice | null, minutes: number): RunNotice => {
  const message = stillWorkingMessage(minutes);

  // Same copy, same object, so a watchdog check that changes nothing does not re-render.
  return current?.kind === 'slow' && current.message === message ? current : { kind: 'slow', message };
};

/** The notice for a run that stayed quiet past the stall timeout. */
const quietNotice = (current: RunNotice | null, serverRunning: boolean, minutes: number): RunNotice => {
  if (current?.kind === 'failed') return current;
  if (serverRunning) return slowNotice(current, minutes);

  return current?.kind === 'stalled' ? current : { kind: 'stalled', message: STALL_MESSAGE };
};

/** While a re-armed watchdog waits, a shown still-working notice keeps its minutes current. */
const refreshSlow = (current: RunNotice | null, minutes: number): RunNotice | null => (
  current?.kind === 'slow' ? slowNotice(current, minutes) : current
);

/** A progress event proves the run is alive, so only a reported failure outlives it. */
const keepReportedFailure = (current: RunNotice | null): RunNotice | null => (
  current?.kind === 'failed' ? current : null
);

const failedNotice = (current: RunNotice | null, reason?: string | null): RunNotice => ({
  kind: 'failed',
  // A second failure event without a reason keeps the reason already shown.
  message: reason?.trim() || (current?.kind === 'slow' ? null : current?.message) || DEFAULT_FAILURE_MESSAGE,
});

const isForProject = (data: unknown, projectId: string | null): boolean => {
  const payload = data as PlanningCompleteEvent | null;

  if (!payload || typeof payload !== 'object') return false;

  // A payload without a project id predates per-project scoping. The SSE stream
  // is already per-project, so treat it as in scope rather than dropping it.
  return !payload.project_id || payload.project_id === projectId;
};

/** Raise a banner when the run reports a terminal failure status. */
const useTerminalFailure = (projectId: string | null, setNotice: SetNotice): void => {
  useEffect(() => {
    if (!projectId) return undefined;

    return eventService.subscribe('planning_complete', (data: unknown) => {
      if (!isForProject(data, projectId)) return;

      const payload = data as PlanningCompleteEvent;
      const status = (payload.status || '').toLowerCase();

      // A non-failure terminal event means the run resolved after all, so it
      // also clears a stall banner raised moments earlier by a slow-but-alive run.
      setNotice((current) => (FAILURE_STATUSES.has(status) ? failedNotice(current, payload.error) : null));
    });
  }, [projectId, setNotice]);
};

/** Track when this project last showed any sign of life. */
const useProgressHeartbeat = (
  projectId: string | null,
  lastProgressRef: MutableRefObject<number>,
  setNotice: SetNotice,
): void => {
  useEffect(() => {
    if (!projectId) return undefined;

    const unsubscribers = PROGRESS_EVENTS.map((eventType) => eventService.subscribe(
      eventType,
      (data: unknown) => {
        if (!isForProject(data, projectId)) return;
        lastProgressRef.current = Date.now();
        setNotice(keepReportedFailure);
      },
    ));

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [projectId, lastProgressRef, setNotice]);
};

interface StallWatch {
  projectId: string | null;
  isRunning: boolean;
  armToken: number;
  lastProgressRef: MutableRefObject<number>;
  setNotice: SetNotice;
  isServerRunning: () => boolean;
}

/**
 * Report a run that has gone quiet for too long. Armed only while running. The
 * stall timeout and the check interval are the panel values in effect when it arms.
 * Re-arming restarts the wait but not the "hasn't reported in N min" clock,
 * which counts from the run's last real progress.
 */
const useStallWatchdog = ({
  projectId, isRunning, armToken, lastProgressRef, setNotice, isServerRunning,
}: StallWatch): void => {
  useEffect(() => {
    if (projectId && isRunning) lastProgressRef.current = Date.now();
  }, [projectId, isRunning, lastProgressRef]);

  useEffect(() => {
    if (!projectId || !isRunning) return undefined;

    const armedAt = Date.now();
    const stallTimeoutMs = getClientTimeout('planningStallTimeoutMs');
    const timer = globalThis.setInterval(() => {
      const now = Date.now();
      const minutes = quietMinutes(lastProgressRef.current, now);
      const tripped = now - Math.max(lastProgressRef.current, armedAt) >= stallTimeoutMs;
      const serverRunning = tripped && isServerRunning();

      setNotice((current) => (tripped ? quietNotice(current, serverRunning, minutes) : refreshSlow(current, minutes)));
    }, getClientTimeout('planningStallCheckIntervalMs'));

    return () => globalThis.clearInterval(timer);
  }, [projectId, isRunning, armToken, lastProgressRef, setNotice, isServerRunning]);
};

/** Notice plus the controls the signal hooks need, scoped to one project. */
const usePlanningFailureState = (projectId: string | null) => {
  // Keyed by project so switching projects drops the previous run's failure
  // during render, rather than through an effect that fires a second render.
  const [failure, setFailure] = useState<{ projectId: string | null; notice: RunNotice | null }>(
    { projectId, notice: null },
  );
  const [armToken, setArmToken] = useState(0);
  const lastProgressRef = useRef<number>(0);

  const setNotice: SetNotice = useCallback((value) => {
    setFailure((current) => {
      const scoped = current.projectId === projectId ? current.notice : null;
      const notice = typeof value === 'function' ? value(scoped) : value;

      return current.projectId === projectId && current.notice === notice ? current : { projectId, notice };
    });
  }, [projectId]);
  const rearm = useCallback(() => setArmToken((token) => token + 1), []);

  return {
    notice: failure.projectId === projectId ? failure.notice : null,
    setNotice,
    rearm,
    armToken,
    lastProgressRef,
  };
};

/** The banner controls: dismiss, or hand a still-running run back to the watchdog. */
const useNoticeControls = (
  setNotice: SetNotice,
  rearm: () => void,
  lastProgressRef: MutableRefObject<number>,
) => {
  const dismiss = useCallback(() => {
    setNotice(null);
    rearm();
  }, [setNotice, rearm]);
  const markStillWorking = useCallback(() => {
    const minutes = quietMinutes(lastProgressRef.current, Date.now());

    setNotice((current) => (current?.kind === 'failed' ? current : slowNotice(current, minutes)));
    rearm();
  }, [setNotice, rearm, lastProgressRef]);

  return { dismiss, markStillWorking };
};

/**
 * @param projectId Project whose run is being watched.
 * @param isRunning Whether the UI currently believes a run is in flight. The
 *                  watchdog arms only while this is true, so an idle project
 *                  never trips it.
 * @param isServerRunning Read when the watchdog trips: whether the server's
 *                  latest report still shows the run's step as running. When
 *                  it does, the notice is `slow` rather than `stalled`.
 */
export function usePlanningRunFailure(
  projectId: string | null,
  isRunning: boolean,
  isServerRunning: () => boolean = NO_SERVER_REPORT,
): PlanningRunFailure {
  const {
    notice, setNotice, rearm, armToken, lastProgressRef,
  } = usePlanningFailureState(projectId);
  const controls = useNoticeControls(setNotice, rearm, lastProgressRef);

  useTerminalFailure(projectId, setNotice);
  useProgressHeartbeat(projectId, lastProgressRef, setNotice);
  useStallWatchdog({
    projectId, isRunning, armToken, lastProgressRef, setNotice, isServerRunning,
  });

  return { message: notice?.message ?? null, kind: notice?.kind ?? null, ...controls };
}
